"use client";

import { useState } from "react";
import { Alert } from "../alert";
import { Knopf } from "../knopf";
import { Feld, type FeldEigenschaften } from "./feld";
import { Auswahl } from "./auswahl";
import { HsAuswahl, SUCHE_AB } from "./erweiterte-auswahl";
import { Trefferfeld, type Treffer } from "./trefferfeld";
import { AVATAR_BILD, AVATAR_INITIALEN, initialen, personSymbolHtml, type HsOption } from "./auswahl-konfiguration";
import { entferne, setzeRolle, type Beteiligung, type BeteiligteRegel } from "./beteiligte-regeln";

/**
 * Personenauswahl (M-002 F7), vier Varianten über `variante`:
 *  - `eine` (P1): genau eine Person, Avatar + Name + Rollenzeile (`untertitel`), „inaktiv“ als Beschreibung.
 *  - `mehrere` (P3): mehrere Personen, Avatar je Option, Zähler „n ausgewählt“, Suche ab etwa 8 Personen.
 *  - `chips` (P5, PM Paket 3b, Katalog `ui-components-cat-tagging-selects/people-tagging-select`): mehrere Personen als Chips mit
 *    Avatar im Eingabefeld (Preline `advanced-select` Modus Tags), Suche durch Tippen, Entfernen am Chip. Für Felder, in denen
 *    die Personen selbst der Wert sind (Mitwirkende, Informierte). Der Aufrufer vergleicht die gemeldete Liste mit dem Stand
 *    und schreibt die Änderung; bei Ablehnung setzt er die Auswahl über `key` auf den Serverstand zurück. Abweichung AW-026
 *    (Initialen statt Fremdbildern, zweite Zeile = Projektrolle).
 *  - `beteiligte` (P4): Personen mit Rolle am Objekt: oben Suchen + Rolle + Hinzufügen, darunter die Liste mit
 *    Rollenwahl und Entfernen je Zeile. Aufgaben: „Verantwortlich · Beteiligt · Informiert“, genau eine Person
 *    verantwortlich (`genauEine`): die zweite Wahl setzt die erste auf `herabstufenAuf` und sagt das.
 *
 * Optionen als Props, kein eigener Datenabruf: Der Aufrufer filtert die Personen mit derselben Prüffunktion wie die
 * Action, die sie später verwendet (r109, r110): die Liste bietet nie großzügiger an, als die Action erlaubt, und
 * blendet still aus (kein „n ausgeblendet“). Avatare: Profilbild (`avatarUrl`, eigener Pfad) oder Initialen, nie
 * Fremdbilder. Der Wert einer P1/P3-Auswahl geht über das native Feld (`name`) ins Formular, der von P4 als
 * JSON in einem versteckten Feld (`name`, Liste von `{ personId, rolle }`).
 */
export interface Person {
  id: string;
  name: string;
  /** Rollenzeile unter dem Namen („Projektleitung“, „Mitglied“). */
  untertitel?: string;
  inaktiv?: boolean;
  /** Profilbild, nur eigener Pfad (beginnt mit `/`); sonst Initialen. */
  avatarUrl?: string | null;
  /** Gruppenüberschrift in der Liste (z. B. Projektrolle). */
  gruppe?: string;
}

export interface PersonenRolle {
  wert: string;
  label: string;
}

function beschreibung(p: Person): string | undefined {
  const teile = [p.untertitel, p.inaktiv ? "inaktiv" : undefined].filter(Boolean);
  return teile.length > 0 ? teile.join(" · ") : undefined;
}

export function personenOptionen(personen: Person[]): HsOption[] {
  return personen.map((p) => ({
    wert: p.id,
    label: p.name,
    beschreibung: beschreibung(p),
    symbolHtml: personSymbolHtml(p.name, p.avatarUrl),
    gruppe: p.gruppe,
  }));
}

type Gemeinsam = FeldEigenschaften & {
  personen: Person[];
  name?: string;
  deaktiviert?: boolean;
};

export type PersonenAuswahlEigenschaften =
  | (Gemeinsam & {
      variante: "eine";
      vorgabe?: string;
      platzhalter?: string;
      /** Leere Option („— kein Zugewiesener —“); ohne sie ist die Auswahl Pflicht und der Aufrufer prüft. */
      leerLabel?: string;
      beiAenderung?: (personId: string) => void;
    })
  | (Gemeinsam & {
      variante: "mehrere";
      vorgabe?: string[];
      platzhalter?: string;
      zaehlerText?: string;
      beiAenderung?: (personIds: string[]) => void;
    })
  | (Gemeinsam & {
      variante: "chips";
      vorgabe?: string[];
      platzhalter?: string;
      beiAenderung?: (personIds: string[]) => void;
    })
  | (Gemeinsam & {
      variante: "beteiligte";
      rollen: PersonenRolle[];
      wert: Beteiligung[];
      beiAenderung: (liste: Beteiligung[]) => void;
      /** Rolle, die höchstens eine Person trägt, und die Rolle, auf die die bisherige Person zurückfällt. */
      genauEine?: string;
      herabstufenAuf?: string;
      hinzufuegenLabel?: string;
    });

export function PersonenAuswahl(props: PersonenAuswahlEigenschaften) {
  if (props.variante === "beteiligte") return <Beteiligte {...props} />;
  if (props.variante === "chips") {
    const { variante, personen, name, deaktiviert, vorgabe, platzhalter, beiAenderung, ...feld } = props;
    void variante;
    return (
      <PersonenListe
        mehrere
        chips
        personen={personen}
        name={name}
        deaktiviert={deaktiviert}
        vorgabe={vorgabe}
        platzhalter={platzhalter}
        beiAenderung={beiAenderung as ((wert: string | string[]) => void) | undefined}
        feld={feld}
      />
    );
  }
  if (props.variante === "mehrere") {
    const { variante, personen, name, deaktiviert, vorgabe, platzhalter, zaehlerText, beiAenderung, ...feld } = props;
    void variante;
    return (
      <PersonenListe
        mehrere
        personen={personen}
        name={name}
        deaktiviert={deaktiviert}
        vorgabe={vorgabe}
        platzhalter={platzhalter}
        zaehlerText={zaehlerText}
        beiAenderung={beiAenderung as ((wert: string | string[]) => void) | undefined}
        feld={feld}
      />
    );
  }
  const { variante, personen, name, deaktiviert, vorgabe, platzhalter, leerLabel, beiAenderung, ...feld } = props;
  void variante;
  return (
    <PersonenListe
      mehrere={false}
      personen={personen}
      name={name}
      deaktiviert={deaktiviert}
      vorgabe={vorgabe}
      platzhalter={platzhalter}
      leerLabel={leerLabel}
      beiAenderung={beiAenderung as ((wert: string | string[]) => void) | undefined}
      feld={feld}
    />
  );
}

/** P1 und P3: die erweiterte Auswahl mit Avatar-Optionen. */
function PersonenListe({
  mehrere,
  chips = false,
  personen,
  name,
  deaktiviert,
  vorgabe,
  platzhalter,
  zaehlerText,
  leerLabel,
  beiAenderung,
  feld,
}: {
  mehrere: boolean;
  /** P5: Chips mit Avatar im Eingabefeld (Modus Tags); die Eingabe im Feld ist die Suche. */
  chips?: boolean;
  personen: Person[];
  name?: string;
  deaktiviert?: boolean;
  vorgabe?: string | string[];
  platzhalter?: string;
  zaehlerText?: string;
  leerLabel?: string;
  beiAenderung?: (wert: string | string[]) => void;
  feld: FeldEigenschaften;
}) {
  const optionen = personenOptionen(personen);
  return (
    <Feld {...feld}>
      {(a) => (
        <HsAuswahl
          attribute={a}
          modus={chips ? "tags" : mehrere ? "mehrfach" : "einzeln"}
          optionen={optionen}
          platzhalter={platzhalter ?? (mehrere ? "Personen wählen" : "Person wählen")}
          mitSuche={!chips && optionen.length >= SUCHE_AB}
          suchePlatzhalter="Person suchen …"
          keineTreffer="Keine passende Person gefunden."
          zaehlerText={zaehlerText}
          leerLabel={leerLabel}
          name={name}
          vorgabe={vorgabe}
          deaktiviert={deaktiviert}
          beiAenderung={beiAenderung}
        />
      )}
    </Feld>
  );
}

function Avatar({ person }: { person: Person }) {
  if (person.avatarUrl && person.avatarUrl.startsWith("/")) {
    // eslint-disable-next-line @next/next/no-img-element
    return <img className={AVATAR_BILD} src={person.avatarUrl} alt="" />;
  }
  return (
    <span className={AVATAR_INITIALEN} aria-hidden="true">
      {initialen(person.name)}
    </span>
  );
}

const HINZUFUEGEN_KNOPF =
  "py-2.5 sm:py-3 px-3 shrink-0 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg bg-layer border border-layer-line text-layer-foreground shadow-2xs hover:bg-layer-hover focus:outline-hidden focus:bg-layer-focus disabled:opacity-50 disabled:pointer-events-none";

/** P4: Beteiligte mit Rolle je Person (kontrolliert; der Aufrufer hält die Liste und prüft beim Speichern). */
function Beteiligte({
  personen,
  rollen,
  wert,
  beiAenderung,
  genauEine,
  herabstufenAuf,
  hinzufuegenLabel = "Hinzufügen",
  name,
  deaktiviert,
  variante: _variante,
  ...feld
}: Extract<PersonenAuswahlEigenschaften, { variante: "beteiligte" }>) {
  void _variante;
  const [neuePerson, setNeuePerson] = useState<Treffer | null>(null);
  const [neueRolle, setNeueRolle] = useState(rollen[0]?.wert ?? "");
  const [hinweis, setHinweis] = useState<string | null>(null);
  const regel: BeteiligteRegel = { genauEine, herabstufenAuf };
  const rolleLabel = (r: string) => rollen.find((x) => x.wert === r)?.label ?? r;
  const person = (id: string) => personen.find((p) => p.id === id);
  const bereits = new Set(wert.map((b) => b.personId));

  const anwenden = (personId: string, rolle: string) => {
    const ergebnis = setzeRolle(wert, personId, rolle, regel);
    beiAenderung(ergebnis.liste);
    if (ergebnis.herabgestuft) {
      const alt = person(ergebnis.herabgestuft.personId)?.name ?? "Die bisherige Person";
      setHinweis(
        `${alt} ist jetzt „${rolleLabel(ergebnis.herabgestuft.rolle)}“. Es kann nur eine Person „${rolleLabel(genauEine ?? "")}“ sein.`,
      );
    } else {
      setHinweis(null);
    }
  };

  // Suchen nur unter den angebotenen Personen, die noch nicht in der Liste stehen (der Aufrufer hat sie schon gefiltert).
  const suche = async (anfrage: string): Promise<Treffer[]> => {
    const q = anfrage.toLocaleLowerCase("de-DE");
    return personen
      .filter((p) => !bereits.has(p.id) && (q === "" || p.name.toLocaleLowerCase("de-DE").includes(q) || (p.untertitel ?? "").toLocaleLowerCase("de-DE").includes(q)))
      .map((p) => ({ id: p.id, label: p.name, beschreibung: beschreibung(p) }));
  };

  return (
    <Feld {...feld} gruppe>
      {(a) => (
        <div role="group" aria-labelledby={a.labelId} aria-describedby={a["aria-describedby"]} className="space-y-3">
          {/* Preline: component/advanced-select/advanced-select-custom-template-with-avatars */}
          {name && <input type="hidden" name={name} value={JSON.stringify(wert)} />}
          {!deaktiviert && (
            <div className="flex flex-col sm:flex-row sm:items-end gap-2">
              <div className="grow">
                <Trefferfeld
                  label="Person suchen"
                  suche={suche}
                  platzhalter="Person suchen …"
                  startText="Tippe zum Suchen."
                  keineTreffer="Keine passende Person gefunden."
                  gewaehlt={neuePerson}
                  beiWahl={setNeuePerson}
                  entfernenLabel="Auswahl entfernen"
                />
              </div>
              <div className="sm:w-44">
                <Auswahl label="Rolle" value={neueRolle} onChange={(e) => setNeueRolle(e.target.value)} optionen={rollen.map((r) => ({ wert: r.wert, label: r.label }))} />
              </div>
              <button
                type="button"
                className={HINZUFUEGEN_KNOPF}
                disabled={!neuePerson}
                onClick={() => {
                  if (!neuePerson) return;
                  anwenden(neuePerson.id, neueRolle);
                  setNeuePerson(null);
                }}
              >
                {hinzufuegenLabel}
              </button>
            </div>
          )}

          {hinweis && <Alert ton="info" id={`${a.id}-umstufung`}>{hinweis}</Alert>}

          {wert.length > 0 && (
            <ul className="divide-y divide-line-2 border border-line-2 rounded-lg">
              {wert.map((b) => {
                const p = person(b.personId);
                const anzeige: Person = p ?? { id: b.personId, name: b.personId };
                return (
                  <li key={b.personId} className="flex items-center gap-x-3 py-2 px-3">
                    <Avatar person={anzeige} />
                    <div className="grow min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{anzeige.name}</p>
                      {p && beschreibung(p) && <p className="text-xs text-muted-foreground-1 truncate">{beschreibung(p)}</p>}
                    </div>
                    <div className="w-36 shrink-0">
                      <Auswahl
                        label={`Rolle von ${anzeige.name}`}
                        labelVersteckt
                        groesse="kompakt"
                        value={b.rolle}
                        disabled={deaktiviert}
                        onChange={(e) => anwenden(b.personId, e.target.value)}
                        optionen={rollen.map((r) => ({ wert: r.wert, label: r.label }))}
                      />
                    </div>
                    {!deaktiviert && (
                      <Knopf
                        variante="ghost"
                        nurSymbol
                        groesse="sm"
                        starr
                        label={`${anzeige.name} entfernen`}
                        onClick={() => {
                          beiAenderung(entferne(wert, b.personId));
                          setHinweis(null);
                        }}
                        symbol={
                          <svg className="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M18 6 6 18" />
                            <path d="m6 6 12 12" />
                          </svg>
                        }
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </Feld>
  );
}
